import { motion } from "framer-motion";
import { FaInstagram, FaLinkedin, FaFacebook } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { Link, useNavigate } from "react-router-dom";
import { IFooterLinks, ISocialLinks } from "Interfaces/interface.footer";
import { ExternalLink, Gift, Globe, Shirt } from "lucide-react";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { environment } from "@/environments/environment";

const Footer: React.FC = () => {
  const navigate = useNavigate();

  const pageLinks: IFooterLinks[] = [
    { label: "Products", url: "/products" },
    { label: "About Us", url: "/about-us" },
    { label: "Visit Corporate Site", url: `${environment.OFFICIAL_SITE_URL}` },
  ];

  const productLinks: IFooterLinks[] = [
    { label: "Brandstore", url: "https://360brandstore.com" },
  ];

  const supportLinks: IFooterLinks[] = [
    {
      label: "Contact Us",
      url: "/contact-us",
    },
  ];

  const socialLinks: ISocialLinks[] = [
    {
      icon: FaLinkedin,
      url: "https://in.linkedin.com/company/360customizer",
      color: "#0068FF",
    },
    {
      icon: FaXTwitter,
      url: "https://twitter.com/360customizer",
      color: "#0068FF",
    },
    {
      icon: FaInstagram,
      url: "https://www.instagram.com/360customizer",
      color: "#0068FF",
    },
    {
      icon: FaFacebook,
      url: "https://www.facebook.com/360customizer",
      color: "#0068FF",
    },
  ];

  const isExternal = (url: string) => url.startsWith("http");

  const handleConnectWithUs = () => {
    window.open(
      "https://sales.zohobookings.in/portal-embed#/273051000000074008",
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <footer className="bg-center bg-no-repeat bg-cover pt-10 md:pt-10 lg:pt-10 relative text-text-primary overflow-hidden">
      <div className="bg-gradient-to-r from-blue-800 via-purple-700 to-[#ab1b59] text-white py-16 px-4 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold mb-4">
          Ready to Gift Smarter, Not Harder?
        </h2>
        <p className="text-md md:text-lg mb-6 text-white/90">
          Join the brands redefining corporate gifting with creativity, ease,
          and impact.
        </p>
        <div className="flex flex-col md:flex-row items-center justify-center gap-4">
          <ButtonSecondary
            variant="info"
            onClick={() => navigate("/products")}
            className="!bg-white text-blue-600 !font-semibold w-60 hover:scale-105"
            icon={<Shirt size={18} />}
            text="Explore Products"
          />
          <ButtonSecondary
            variant="info"
            onClick={handleConnectWithUs}
            className="!bg-white text-blue-600 !font-semibold w-60 hover:scale-105"
            icon={<Globe size={18} />}
            text="Lets's Talk"
          />
        </div>
      </div>
      <div className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 text-white py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-white/5 to-white/10"></div>
        <div className="absolute inset-0 opacity-20"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 shadow-2xl">
            {/* <div className="grid md:grid-cols-4 gap-8"> */}
            <div className="grid md:grid-cols-4 gap-8 text-center md:text-left">
              <div>
                <div className="flex flex-col items-center md:flex-row md:items-center md:space-x-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-400 rounded-xl flex items-center justify-center mx-auto md:mx-0">
                    <Gift className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <span className="font-bold text-xl">360GiftHub</span>
                    <p className="text-xs text-gray-300">
                      Driven by Purpose. Delivered with Care.
                    </p>
                  </div>
                </div>
                <p className="text-gray-300 mb-4 text-sm">
                  End-to-end gifting solutions tailored for teams, brands, and
                  every occasion.
                </p>
                <div className="flex justify-center md:justify-start space-x-6 md:space-x-0 md:grid md:grid-cols-2 gap-x-4 gap-y-4 xl:flex">
                  {socialLinks.map((link, index) => {
                    const Icon = link.icon as React.FC;
                    return (
                      <motion.a
                        key={index}
                        href={link.url}
                        className="text-[26px] text-text-secondary hover:text-primary"
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{
                          scale: 1.2,
                          textShadow: "0 0 10px var(--glow-primary)",
                          color: `${link.color}`,
                        }}
                        transition={{ duration: 0.3 }}
                      >
                        <Icon />
                      </motion.a>
                    );
                  })}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-6 text-lg">Explore</h4>
                <ul className="space-y-3 text-gray-300 text-center md:text-left">
                  {pageLinks.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.url}
                        className="hover:text-white transition-colors"
                        {...(isExternal(link.url)
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                      >
                        <div className="flex items-center justify-center md:justify-start space-x-1">
                          <span>{link.label}</span>
                          {link.label === "Visit Corporate Site" && (
                            <ExternalLink className="w-4 h-4" />
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-6 text-lg">Other Solutions</h4>
                <ul className="space-y-3 text-gray-300">
                  {productLinks.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.url}
                        className="hover:text-white transition-colors"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-6 text-lg">Support</h4>
                <ul className="space-y-3 text-gray-300">
                  {supportLinks.map((link) => (
                    <li key={link.label}>
                      <Link
                        to={link.url}
                        className="hover:text-white transition-colors"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <hr className="my-8 text-white/20" />
            <div className="text-center text-gray-300 flex flex-col md:flex-row justify-center md:justify-between items-center gap-6">
              <p>&copy; 2025 Namrup Technologies Private Limited.</p>
              <div className="text-sm font-inter">
                <Link
                  to="privacy-policy"
                  className="hover:text-white transition-colors"
                >
                  Privacy Policy
                </Link>
                <span> | </span>
                <Link
                  to="/terms-of-use"
                  className="hover:text-white transition-colors"
                >
                  Terms of Use
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
